import React, { useState, useRef, useEffect } from "react";
import {
  Breadcrumbs,
  Select,
  FormControl,
  MenuItem,
  Container,
  CircularProgress,
  Backdrop,
  InputLabel,
  Link,
  FormControlLabel,
  RadioGroup,
  Radio,
  Grid,
  Button,
} from "@material-ui/core";
import moment from "moment";

// styles
import useStyles from "./styles";

// components
import PageTitle from "../../components/PageTitle/PageTitle";
import { Typography } from "../../components/Wrappers/Wrappers";
import Widget from "../../components/Widget/Widget";

//context
import { useVendorState } from "../../context/VendorContext";
import { tokenConfig } from "../../context/UserContext";
import PieChart from "./components/pieChart";
import LineBarChart from "./components/lineBarChart";

//helpers
import { cloneAndSortDescending } from "../../healpers/dateRange.helpers";
import { getMonthDatesBetween, getQuarterDatesBetween, getWeekDatesBetween, getYearDatesBetween } from "../../healpers/utilityFunctions";
import { BASE_URL } from "../../healpers/api";
import api from "../../healpers/apiRoutes";
import axios from "axios";
import _ from "lodash";

export default function PlacementAnalysis(props) {
  var classes = useStyles();

  //global
  var vendorState = useVendorState();
  // local
  const [campaignTypes] = useState(["SP", "SB"]);
  const [selectedCampaignType, setSelectedCampaignType] = useState("SP");
  const [fullPageLoader, setFullPageLoader] = useState(false);
  const [radioValue, setRadioValue] = useState("monthly");
  const [dateRange, setDateRange] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState(1);
  const [weekRange, setWeekRange] = useState(null);
  const [monthRange, setMonthRange] = useState(null);
  const [quarterRange, setQuarterRange] = useState(null);
  const [annualRange, setAnnualRange] = useState(null);
  const [range, setRange] = useState({
    startDate: new Date(moment("01-01-2023", "DD-MM-YYYY")),
    endDate: new Date(),
  });
  const [salesChartData, setSalesChartData] = useState([]);
  const [returnsChartData, setReturnsChartData] = useState([]);

  useEffect(() => {
    if (range) {
      const weekDateRange = cloneAndSortDescending([...getWeekDatesBetween(new Date(range.startDate), new Date(range.endDate))]);
      const monthDateRange = cloneAndSortDescending([...getMonthDatesBetween(new Date(range.startDate), new Date(range.endDate))]);
      const quarterDateRange = cloneAndSortDescending([...getQuarterDatesBetween(new Date(range.startDate), new Date(range.endDate))]);
      const annualDateRange = cloneAndSortDescending([...getYearDatesBetween(new Date(range.startDate), new Date(range.endDate))]);
      setDateRange(monthDateRange);
      if (monthDateRange.length !== 0) setSelectedDateRange(1);

      setWeekRange(weekDateRange);
      setMonthRange(monthDateRange);
      setQuarterRange(quarterDateRange);
      setAnnualRange(annualDateRange);
    }
    return () => {
      setDateRange(null);
      setWeekRange([]);
      setMonthRange([]);
      setQuarterRange([]);
      setAnnualRange([]);
    };
  }, [range]);

  const handleRadioChange = (event) => {
    setRadioValue(event.target.value);
    if (event.target.value === "weekly") {
      setDateRange(weekRange);
      setSelectedDateRange(1);
    } else if (event.target.value === "monthly") {
      setDateRange(monthRange);
      setSelectedDateRange(1);
    } else if (event.target.value === "quarterly") {
      setDateRange(quarterRange);
      setSelectedDateRange(1);
    } else {
      setDateRange(annualRange);
      setSelectedDateRange(0);
    }
  };

  useEffect(() => {
    if(vendorState.selected) {
      loadData()
    }
  }, [vendorState.selected, selectedDateRange, dateRange, selectedCampaignType])

  async function loadData() {
    try {
      setFullPageLoader(true);
      let params = {
        vendorCode: vendorState?.selected,
        startDate: moment(dateRange[selectedDateRange].startdate).format("MM-DD-YYYY"),
        endDate: moment(dateRange[selectedDateRange].enddate).format("MM-DD-YYYY"),
        campaignType: selectedCampaignType,
      };
      const result = await axios.get(BASE_URL + api.placementAnalysis, tokenConfig(params))
      parseReports(result.data);
    } catch (error) {
      console.log(error);
    } finally {
      setFullPageLoader(false);
    }
  }

  const parseReports = (reports) => {
    let totalSales = 0;
    const salesOutput = {
      "Top of Search on-Amazon": 0,
      "Detail Page on-Amazon": 0,
      "Other on-Amazon": 0,
    }
    const costOutput = {
      "Top of Search on-Amazon": 0,
      "Detail Page on-Amazon": 0,
      "Other on-Amazon": 0,
    }
    reports.forEach((report) => {
      if (report.placement === "Top of Search on-Amazon") {
        salesOutput["Top of Search on-Amazon"] += report.sales;
        costOutput["Top of Search on-Amazon"] += report.cost;
      } else if (report.placement === "Detail Page on-Amazon") {
        salesOutput["Detail Page on-Amazon"] += report.sales;
        costOutput["Detail Page on-Amazon"] += report.cost;
      } else {
        salesOutput["Other on-Amazon"] += report.sales;
        costOutput["Other on-Amazon"] += report.cost;
      }
      totalSales += report.sales;
    });
    if(reports.length) {
      setSalesChartData([
        { name: "Top of Search", y: Math.round((salesOutput["Top of Search on-Amazon"]/totalSales) * 100)},
        { name: "Product Page", y: Math.round((salesOutput["Detail Page on-Amazon"]/totalSales) * 100)},
        { name: "Rest of Search", y: Math.round((salesOutput["Other on-Amazon"]/totalSales) * 100)},
      ])
      setReturnsChartData([
        parseFloat((salesOutput["Top of Search on-Amazon"]/costOutput["Top of Search on-Amazon"]).toFixed(2)),
        salesOutput["Detail Page on-Amazon"] ? parseFloat((salesOutput["Detail Page on-Amazon"]/costOutput["Detail Page on-Amazon"]).toFixed(2)) : 0,
        parseFloat((salesOutput["Other on-Amazon"]/costOutput["Other on-Amazon"]).toFixed(2)),
      ])
    } else {
      setSalesChartData([])
      setReturnsChartData([])
    }
  }

  return (
    <Container maxWidth={false}>
      <PageTitle
        title="Placement Analysis"
        breadCrump={
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" className={classes.link}>
              AMS Reports
            </Link>
            <Typography className={classes.link} color="primary">
              Placement Analysis
            </Typography>
          </Breadcrumbs>
        }
        rightAlignComponent={
          <Grid container style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", flexWrap: "nowrap" }}>
            <FormControl>
              <RadioGroup value={radioValue} onChange={handleRadioChange}>
                <FormControlLabel
                  value="weekly"
                  control={<Radio color="primary" />}
                  label="Weekly"
                />
                <FormControlLabel
                  value="monthly"
                  control={<Radio color="primary" />}
                  label="Monthly"
                />
              </RadioGroup>
            </FormControl>
            <FormControl>
              <RadioGroup value={radioValue} onChange={handleRadioChange}>
                <FormControlLabel
                  value="quarterly"
                  control={<Radio color="primary" />}
                  label="Quarterly"
                />
                <FormControlLabel
                  value="yearly"
                  control={<Radio color="primary" />}
                  label="Yearly"
                />
              </RadioGroup>
            </FormControl>
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel id="demo-simple-select-outlined-label">
                Select Range
              </InputLabel>
              <Select
                value={selectedDateRange}
                onChange={(e) => {
                  setSelectedDateRange(e.target.value);
                }}
                label="Select Range"
                disabled={dateRange && dateRange.length === 0 ? true : false}
              >
                {dateRange ? (
                  dateRange.map((range, index) => {
                    return (
                      <MenuItem value={index}>
                        {range.startdate + " - " + range.enddate}
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem value={0}>
                    <i>No Dates</i>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel id="demo-simple-select-outlined-label">Campaign Type</InputLabel>
              <Select
                defaultValue={selectedCampaignType}
                value={selectedCampaignType}
                onChange={(e) => setSelectedCampaignType(e.target.value)}
                label="Campaign Type"
                disabled={campaignTypes && campaignTypes.length === 0 ? true : false}
                className={classes.select}
              >
                {campaignTypes ? (
                  campaignTypes.map((type) => {
                    return <MenuItem value={type}>{type}</MenuItem>;
                  })
                ) : null }
              </Select>
            </FormControl>
          </Grid>
        }
      />
      <Grid container style={{ marginBottom: 30 }}>
        <Grid item xs={12}>
          <Widget
            upperTitle
            noBodyPadding
            bodyClass={classes.tableOverflow}
            header={
              <div className={classes.mainChartHeader}>
                <Typography
                  variant="h2"
                  gutterBottom
                  style={{
                    fontWeight: 600,
                    fontSize: 20,
                    color: "black",
                  }}
                >
                  Placement by Sales
                </Typography>
              </div>
            }
          >
            <PieChart
              data={salesChartData}
            />
          </Widget>
        </Grid>
      </Grid>

      <Grid container style={{ marginBottom: 30 }}>
        <Grid item xs={12}>
          <Widget
            upperTitle
            noBodyPadding
            bodyClass={classes.tableOverflow}
            header={
              <div className={classes.mainChartHeader}>
                <Typography
                  variant="h2"
                  gutterBottom
                  style={{
                    fontWeight: 600,
                    fontSize: 20,
                    color: "black",
                  }}
                >
                  Placement by Returns on Ad Spent
                </Typography>
              </div>
            }
          >
            <LineBarChart
              labels={["Top of Search", "Product Page", "Rest of Search"]}
              data={returnsChartData}
            />
          </Widget>
        </Grid>
      </Grid>

      <Backdrop
        className={classes.backdrop}
        open={fullPageLoader}
        onClick={() => setFullPageLoader(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
}
